import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { MapContainer, TileLayer, Popup, CircleMarker, useMap } from "react-leaflet";
import { Maximize2, X, Hand } from "lucide-react";
import { markerHtml } from "@/lib/icons";
import { useServiceMap } from "@/lib/data";
import { km } from "@/lib/api";
import { brl, featuredPrice, withDiscount } from "@/lib/prices";

const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const IS_MOBILE = L.Browser.mobile;

// Always-visible balloon above the dot (featured price) + round "-X%" badge on the dot.
function tagHtml(price, off) {
  const badge = off > 0 ? `<span class="po-off">-${off}%</span>` : "";
  if (!price) return badge;
  const value = off > 0
    ? `<b class="po-tag-new">${brl(withDiscount(price.value, off))}</b><s class="po-tag-old">${brl(price.value)}</s>`
    : `<b class="po-tag-price">${brl(price.value)}</b>`;
  return `<span class="po-tag" data-off="${off}"><span class="po-tag-name">${esc(price.name)}</span><span class="po-tag-row">${value}</span></span>${badge}`;
}

const iconCache = {};
function iconFor(icon, off = 0, price = null) {
  const key = `${icon}|${off}|${price ? `${price.name}|${price.value}` : ""}`;
  if (!iconCache[key]) {
    iconCache[key] = L.divIcon({ className: "po-marker", html: markerHtml(icon) + tagHtml(price, off), iconSize: [44, 44], iconAnchor: [22, 22], popupAnchor: [0, -22] });
  }
  return iconCache[key];
}

// Hide balloons that would cover another balloon or another dot; keep balloons inside the map horizontally.
function declutter(map) {
  const root = map.getContainer();
  const box = root.getBoundingClientRect();
  const dots = [...root.querySelectorAll(".leaflet-marker-icon.po-marker")];
  const dotRects = dots.map((d) => [d, d.getBoundingClientRect()]);
  const hits = (r, s) => r.left < s.right + 2 && r.right > s.left - 2 && r.top < s.bottom + 2 && r.bottom > s.top - 2;
  const shown = [];
  // Highest discount wins when balloons collide.
  [...root.querySelectorAll(".po-tag")].sort((a, b) => Number(b.dataset.off || 0) - Number(a.dataset.off || 0)).forEach((el) => {
    el.style.visibility = "";
    el.style.marginLeft = "";
    el.classList.remove("po-tag-below");
    let r = el.getBoundingClientRect();
    if (r.top < box.top + 4) { el.classList.add("po-tag-below"); r = el.getBoundingClientRect(); }
    const shift = r.right > box.right - 4 ? box.right - 4 - r.right : r.left < box.left + 4 ? box.left + 4 - r.left : 0;
    if (shift) { el.style.marginLeft = `${shift}px`; r = el.getBoundingClientRect(); }
    const own = el.closest(".leaflet-marker-icon");
    const blocked = shown.some((s) => hits(r, s)) || dotRects.some(([d, s]) => d !== own && hits(r, s));
    if (blocked) el.style.visibility = "hidden";
    else shown.push(r);
  });
}

// Deterministic small spread (60-220 m) around the neighborhood centre; never the real address.
function spread(p) {
  let h = 0;
  for (const c of p.id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const angle = (h % 360) * (Math.PI / 180);
  const meters = 60 + (h % 160);
  return [p.lat + (meters / 111000) * Math.cos(angle), p.lng + (meters / (111000 * Math.cos((p.lat * Math.PI) / 180))) * Math.sin(angle)];
}

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function ClusterLayer({ providers, serviceSlug }) {
  const map = useMap();
  const services = useServiceMap();
  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 45,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (c) => L.divIcon({ className: "po-marker", html: `<div class="po-cluster">${c.getChildCount()}</div>`, iconSize: [48, 48] }),
    });
    providers.forEach((p) => {
      const slug = serviceSlug && p.services.includes(serviceSlug) ? serviceSlug : p.primary_service;
      const svc = services[slug];
      const rating = p.rating_count ? `★ ${String(p.rating_avg).replace(".", ",")}` : "Novo";
      const off = Number(p.discount_pct) || 0;
      const price = featuredPrice(p);
      const m = L.marker(spread(p), { icon: iconFor(svc?.icon, off, price) });
      const priceLine = price
        ? `<span style="color:#334155">${esc(price.name)}:</span> ${off > 0 ? `<s style="color:#94a3b8">${brl(price.value)}</s> <b style="color:#16a34a">${brl(withDiscount(price.value, off))}</b>` : `<b style="color:#ea580c">${brl(price.value)}</b>`}<br/>`
        : "";
      m.bindPopup(`<div style="font-size:16px;min-width:160px"><b>${esc(svc?.name || "Serviço")}</b><br/>${priceLine}${off > 0 ? `<b style="color:#15803d">${off}% OFF no orçamento</b><br/>` : ""}Aprox. ${esc(km(p.distance_km))} de você<br/><span style="color:#64748b">${esc(rating)} · ${esc(p.base_neighborhood)}</span><br/><a href="/prestador/${esc(p.id)}" style="color:#ea580c;font-weight:700">Ver perfil</a></div>`);
      group.addLayer(m);
    });
    map.addLayer(group);
    let raf = 0;
    const tidy = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => declutter(map)); };
    map.on("zoomend moveend", tidy);
    group.on("animationend spiderfied unspiderfied", tidy);
    const t = setTimeout(tidy, 300);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); map.off("zoomend moveend", tidy); map.removeLayer(group); };
  }, [map, providers, services, serviceSlug]);
  return null;
}

// Frames all providers (padding for balloons); stops after a manual move until the list changes or "Ver todos".
function AutoFit({ center, providers }) {
  const map = useMap();
  const [moved, setMoved] = useState(false);
  const fitting = useRef(false);
  const ids = providers.map((p) => p.id).join(",");
  const fit = useCallback(() => {
    fitting.current = true;
    const pts = providers.map(spread);
    if (pts.length === 0) map.setView(center, 12);
    else if (pts.length === 1) map.setView(pts[0], 15);
    else map.fitBounds(L.latLngBounds(pts), { paddingTopLeft: [40, 90], paddingBottomRight: [40, 45], maxZoom: 16 });
    setMoved(false);
    setTimeout(() => { fitting.current = false; }, 400);
  }, [map, center, providers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fit(); }, [ids, map]);
  useEffect(() => {
    const onUser = () => { if (!fitting.current) setMoved(true); };
    map.on("dragstart zoomstart", onUser);
    return () => { map.off("dragstart zoomstart", onUser); };
  }, [map]);
  if (!moved || providers.length === 0) return null;
  return (
    <button type="button" onClick={fit} data-testid="map-fit-all"
      className="absolute bottom-3 right-3 z-[1000] min-h-[40px] rounded-full bg-white px-4 text-base font-bold text-orange-600 shadow-lg ring-1 ring-slate-200">
      Ver todos
    </button>
  );
}

// On phones: one finger scrolls the page, two fingers move/zoom the map.
function TwoFingerHint() {
  const map = useMap();
  const [show, setShow] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    const el = map.getContainer();
    const onMove = (e) => {
      if (e.touches.length !== 1) return;
      setShow(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setShow(false), 1500);
    };
    el.addEventListener("touchmove", onMove, { passive: true });
    return () => { el.removeEventListener("touchmove", onMove); clearTimeout(timer.current); };
  }, [map]);
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-[1000] flex items-center justify-center bg-slate-900/45" data-testid="map-two-finger-hint">
      <p className="flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-base font-bold text-slate-800 shadow-lg"><Hand className="h-5 w-5 text-orange-500" /> Use dois dedos para mover o mapa</p>
    </div>
  );
}

function MapView({ center, providers, serviceSlug, full = false }) {
  const twoFinger = IS_MOBILE && !full;
  return (
    <MapContainer key={center.join(",")} center={center} zoom={12} scrollWheelZoom={full} dragging={!twoFinger} tap={false}
      className={`po-map ${full ? "h-full" : "h-[220px] sm:h-[380px]"}`} style={{ width: "100%" }}>
      <TileLayer attribution={ATTRIBUTION} url={TILES} maxZoom={19} />
      <CircleMarker center={center} radius={7} pathOptions={{ color: "#FFFFFF", fillColor: "#F97316", fillOpacity: 1, weight: 3 }}>
        <Popup>Você está aqui (aproximado)</Popup>
      </CircleMarker>
      <ClusterLayer providers={providers} serviceSlug={serviceSlug} />
      <AutoFit center={center} providers={providers} />
      {twoFinger && <TwoFingerHint />}
    </MapContainer>
  );
}

function FullscreenMap({ onClose, ...props }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[900] bg-white" data-testid="map-fullscreen">
      <MapView {...props} full />
      <button onClick={onClose} aria-label="Fechar mapa" data-testid="map-fullscreen-close"
        className="absolute right-4 top-4 z-[1000] flex h-14 w-14 items-center justify-center rounded-full bg-white text-slate-800 shadow-lg ring-1 ring-slate-200 hover:bg-slate-50">
        <X className="h-7 w-7" />
      </button>
    </div>
  );
}

export default function ProviderMap({ origin, providers, serviceSlug }) {
  const center = useMemo(() => [origin.lat, origin.lng], [origin.lat, origin.lng]);
  const [full, setFull] = useState(false);
  return (
    <div data-testid="provider-map">
      <div className="relative overflow-hidden rounded-3xl shadow-md ring-1 ring-slate-200">
        <MapView center={center} providers={providers} serviceSlug={serviceSlug} />
      </div>
      <button onClick={() => setFull(true)} className="mt-2 inline-flex min-h-[48px] items-center gap-2 rounded-xl px-2 text-lg font-semibold text-orange-600 hover:text-orange-700" data-testid="map-expand-button">
        <Maximize2 className="h-5 w-5" /> Ver mapa maior
      </button>
      {full && <FullscreenMap center={center} providers={providers} serviceSlug={serviceSlug} onClose={() => setFull(false)} />}
    </div>
  );
}
